import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { state } from '../store/global';
import { getFinancialMap, generateMapVoiceAudio, sendMapWhatsapp, fmtCop } from '../lib/goals';
import SectionBanner from '../components/SectionBanner';

const NAVY = '#0A2540';
const GOLD = '#C9A961';

const usd = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

/**
 * MiMapa — "El mapa de tu dinero" (Gemelo Financiero). El cierre de la ontologia:
 * todo el grafo del cliente (inversiones, saldo libre, metas, macro) en una vista,
 * con la DECISION mas importante arriba ("tu proximo mejor paso").
 */
export default function MiMapa() {
  const navigate = useNavigate();
  const snap = useSnapshot(state);
  const userPhone = snap.user?.phonenumber
    ? `${snap.user.phonenumber.nationality?.area_code || ''}${snap.user.phonenumber.number || ''}`
    : '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [listenLoading, setListenLoading] = useState(false);
  const [listenUrl, setListenUrl] = useState(null);
  const [waLoading, setWaLoading] = useState(false);
  const [voiceMsg, setVoiceMsg] = useState(null);

  useEffect(() => {
    let alive = true;
    getFinancialMap()
      .then((d) => alive && setData(d))
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const handleListen = async () => {
    if (listenLoading) return;
    setListenLoading(true); setVoiceMsg(null);
    try {
      const res = await generateMapVoiceAudio();
      if (res?.audioUrl) setListenUrl(`${res.audioUrl}?t=${Date.now()}`);
      else setVoiceMsg({ ok: false, text: 'No se pudo generar el audio.' });
    } catch (e) {
      setVoiceMsg({ ok: false, text: e.response?.data?.message || 'No se pudo generar el audio.' });
    } finally { setListenLoading(false); }
  };

  const handleWhatsapp = async () => {
    if (!userPhone) { setVoiceMsg({ ok: false, text: 'No tienes un teléfono registrado en tu perfil.' }); return; }
    setWaLoading(true); setVoiceMsg(null);
    try {
      await sendMapWhatsapp(userPhone);
      setVoiceMsg({ ok: true, text: 'Listo, te enviamos el mapa con voz a WhatsApp.' });
    } catch (e) {
      setVoiceMsg({ ok: false, text: e.response?.data?.message || 'No pudimos enviarlo.' });
    } finally { setWaLoading(false); setTimeout(() => setVoiceMsg(null), 6000); }
  };

  if (loading) return <div className="mapa-ola px-4 sm:px-6 lg:px-8 py-8 text-sm" style={{ color: 'var(--fx-muted)' }}>Armando tu mapa...</div>;
  if (!data) return <div className="mapa-ola px-4 sm:px-6 lg:px-8 py-8 text-sm" style={{ color: 'var(--fx-muted)' }}>No pudimos cargar tu mapa. Intenta de nuevo.</div>;

  const { patrimonio, metas, macro, nextBestAction: nba } = data;

  return (
    <div className="mapa-ola px-4 sm:px-6 lg:px-8 py-6 max-w-3xl mx-auto">
      <div className="mb-5">
        <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--fx-text)' }}>El mapa de tu dinero</h1>
        <p className="text-sm" style={{ color: 'var(--fx-muted)' }}>Todo tu dinero y tus metas en un solo lugar. El Oráculo lo mira completo por ti.</p>
      </div>

      <SectionBanner
        id="mimapa"
        title="Tu vida financiera, conectada"
        text="Tus inversiones, tu saldo y tus metas no son islas: el Oráculo ve cómo se relacionan y te dice el mejor próximo paso."
      />

      {/* TU PRÓXIMO MEJOR PASO — la decisión global del Oráculo */}
      <div className="rounded-2xl px-6 py-5 mb-6 text-white" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #1A3A6C 100%)` }}>
        <div className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: GOLD }}>
          Tu próximo mejor paso
        </div>
        <div className="text-lg font-bold leading-tight mb-1">{nba.titulo}</div>
        <p className="text-sm text-white/80 leading-relaxed mb-4">{nba.detalle}</p>
        <button
          onClick={() => navigate(nba.accion)}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{ background: GOLD, color: NAVY }}
        >
          {nba.cta} →
        </button>
      </div>

      {/* El mapa, contado con voz por el Oráculo (Daniela) */}
      <div className="mb-6">
        <div className="text-xs font-medium mb-2" style={{ color: 'var(--fx-muted)' }}>¿Prefieres que el Oráculo te lo cuente?</div>
        <div className="flex flex-col sm:flex-row gap-2">
          <button onClick={handleListen} disabled={listenLoading}
            className="flex items-center justify-center gap-2 flex-1 py-3 rounded-xl text-white text-sm font-medium disabled:opacity-60" style={{ background: NAVY }}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
            {listenLoading ? 'Generando con voz...' : 'Escuchar mi mapa'}
          </button>
          <button onClick={handleWhatsapp} disabled={waLoading}
            className="flex items-center justify-center gap-2 flex-1 py-3 rounded-xl text-white text-sm font-medium disabled:opacity-60" style={{ background: '#25D366' }}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-1.207zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>
            {waLoading ? 'Enviando...' : 'Enviar a WhatsApp'}
          </button>
        </div>
        {listenUrl && <audio controls autoPlay preload="none" src={listenUrl} className="w-full mt-3" style={{ height: 40 }} />}
        {voiceMsg && <div className={`text-xs mt-2 text-center ${voiceMsg.ok ? 'text-[#166534]' : 'text-red-600'}`}>{voiceMsg.text}</div>}
      </div>

      {/* EL GRAFO: los nodos de tu dinero */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="mapa-ola__card px-5 py-4">
          <div className="text-xs mb-1" style={{ color: 'var(--fx-muted)' }}>Mis inversiones</div>
          <div className="text-xl font-bold" style={{ color: 'var(--fx-text)' }}>{usd(patrimonio.totalInvertidoUsd)} <span className="text-xs font-normal" style={{ color: 'var(--fx-muted)' }}>USD</span></div>
          {patrimonio.dailyPnlPct != null && (
            <div className={`text-xs font-medium mt-0.5 ${patrimonio.dailyPnlPct >= 0 ? 'text-[#059669]' : 'text-[#DC2626]'}`}>
              {patrimonio.dailyPnlPct >= 0 ? '+' : ''}{patrimonio.dailyPnlPct}% hoy
            </div>
          )}
        </div>
        <div className="mapa-ola__card px-5 py-4">
          <div className="text-xs mb-1" style={{ color: 'var(--fx-muted)' }}>Saldo libre</div>
          <div className="text-xl font-bold" style={{ color: 'var(--fx-text)' }}>{usd(patrimonio.saldoLibreUsd)} <span className="text-xs font-normal" style={{ color: 'var(--fx-muted)' }}>USD</span></div>
          <div className="text-xs mt-0.5" style={{ color: 'var(--fx-muted)' }}>listo para invertir</div>
        </div>
        <div className="mapa-ola__card px-5 py-4">
          <div className="text-xs mb-1" style={{ color: 'var(--fx-muted)' }}>Mis metas</div>
          <div className="text-xl font-bold" style={{ color: 'var(--fx-text)' }}>{metas.total}</div>
          <div className="text-xs mt-0.5" style={{ color: 'var(--fx-muted)' }}>
            {metas.valorTotalCop > 0 ? `${fmtCop(metas.valorTotalCop)} acumulado` : 'crea la primera'}
          </div>
        </div>
      </div>

      {/* Contexto macro */}
      {macro?.inflacionPct != null && (
        <div className="text-xs text-center" style={{ color: 'var(--fx-muted)' }}>
          Contexto: la inflación de Colombia va en <b style={{ color: 'var(--fx-text)' }}>{macro.inflacionPct}%</b> al año (Panorama de Progresión).
          Tu dinero invertido le hace frente.
        </div>
      )}
    </div>
  );
}
